import React from 'react';
import { Box, TextField, Typography } from '@mui/material';

import { ParamOverrides } from '../../types/admin';
import { splitModelConfig } from './llm-catalog/modelConfig';

interface ParamOverridesFieldsProps {
  value?: ParamOverrides | null;
  onChange: (value: ParamOverrides | null) => void;
  /** Raw `LLMModel.config` of the assigned model; its defaults are shown as placeholders. */
  modelConfig?: string | null;
  disabled?: boolean;
}

type Key = keyof ParamOverrides;

/**
 * Optional per-assignment generation parameters (temperature, max output tokens, top P).
 * Empty = inherit the model default from the LLM Catalog (then the provider default).
 */
export const ParamOverridesFields: React.FC<ParamOverridesFieldsProps> = ({
  value,
  onChange,
  modelConfig,
  disabled = false,
}) => {
  const defaults = React.useMemo(() => splitModelConfig(modelConfig).typed, [modelConfig]);

  const setKey = (key: Key, raw: string, integer = false) => {
    const next: ParamOverrides = { ...(value ?? {}) };
    const parsed = integer ? parseInt(raw, 10) : parseFloat(raw);
    if (raw === '' || !Number.isFinite(parsed)) delete next[key];
    else next[key] = parsed;
    onChange(Object.keys(next).length > 0 ? next : null);
  };

  const placeholder = (modelDefault: number | null) =>
    modelDefault === null ? 'Provider default' : `Model: ${modelDefault}`;

  return (
    <Box className="md:col-span-2">
      <Typography variant="subtitle2" className="font-semibold">
        Parameter overrides (optional)
      </Typography>
      <Typography variant="caption" className="mb-3 block text-gray-500 dark:text-slate-400">
        Set values for this use case only (e.g. low temperature for coding, higher for chat). Empty
        = use the model default from the LLM Catalog.
      </Typography>
      <Box className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <TextField
          size="small"
          type="number"
          label="Temperature"
          value={value?.temperature ?? ''}
          disabled={disabled}
          placeholder={placeholder(defaults.defaultTemperature)}
          onChange={(event) => setKey('temperature', event.target.value)}
          inputProps={{ min: 0, max: 2, step: 0.1 }}
          helperText="0.0 - 2.0"
        />
        <TextField
          size="small"
          type="number"
          label="Max Output Tokens"
          value={value?.maxOutputTokens ?? ''}
          disabled={disabled}
          placeholder={placeholder(defaults.defaultMaxOutputTokens)}
          onChange={(event) => setKey('maxOutputTokens', event.target.value, true)}
          inputProps={{ min: 1, step: 128 }}
        />
        <TextField
          size="small"
          type="number"
          label="Top P"
          value={value?.topP ?? ''}
          disabled={disabled}
          placeholder={placeholder(defaults.defaultTopP)}
          onChange={(event) => setKey('topP', event.target.value)}
          inputProps={{ min: 0, max: 1, step: 0.05 }}
          helperText="0.0 - 1.0"
        />
      </Box>
    </Box>
  );
};
